import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useCustomerAuth } from "./CustomerAuthContext";
import { apiRequest } from "../services/api";

const WishlistContext = createContext(null);
export function WishlistProvider({ children }) {
  const { customer, accessToken, isAuthenticated } = useCustomerAuth();
  const owner = isAuthenticated ? customer?.id : null;
  const authRef = useRef({ owner, accessToken });
  authRef.current = { owner, accessToken };
  const version = useRef(0);
  const locks = useRef(new Set());
  const itemsRef = useRef([]);
  const [state, setState] = useState({
    owner: null,
    items: [],
    optimistic: {},
    pending: {},
    ready: false,
    loading: false,
    error: "",
  });
  const refresh = useCallback(async () => {
    if (!owner || !accessToken || locks.current.size) return;
    const revision = ++version.current;
    setState((previous) =>
      previous.owner === owner
        ? { ...previous, loading: true, error: "" }
        : {
            owner,
            items: [],
            optimistic: {},
            pending: {},
            ready: false,
            loading: true,
            error: "",
          },
    );
    try {
      const items = await apiRequest("/customer-wishlist", { accessToken });
      if (revision !== version.current || authRef.current.owner !== owner)
        return;
      itemsRef.current = items || [];
      setState((previous) => ({
        ...previous,
        owner,
        items: itemsRef.current,
        ready: true,
        loading: false,
        error: "",
      }));
    } catch (error) {
      if (revision !== version.current || authRef.current.owner !== owner)
        return;
      setState((previous) => ({
        ...previous,
        loading: false,
        error: error.message || "Unable to load your wishlist. Please retry.",
      }));
    }
  }, [owner, accessToken]);
  useEffect(() => {
    locks.current.clear();
    itemsRef.current = [];
    setState({
      owner,
      items: [],
      optimistic: {},
      pending: {},
      ready: false,
      loading: false,
      error: "",
    });
    void refresh();
    return () => {
      version.current++;
    };
  }, [owner, refresh]);
  useEffect(() => {
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);
  const change = useCallback(
    async (variantId, desired) => {
      if (!owner || !accessToken) return false;
      if (!variantId || locks.current.has(variantId)) return false;
      const sessionOwner = owner;
      locks.current.add(variantId);
      version.current++; // Ignore reads started before this mutation.
      setState((previous) => ({
        ...previous,
        optimistic: { ...previous.optimistic, [variantId]: desired },
        pending: { ...previous.pending, [variantId]: true },
        loading: false,
        error: "",
      }));
      try {
        const result = await apiRequest(
          desired
            ? "/customer-wishlist"
            : "/customer-wishlist/" + encodeURIComponent(variantId),
          {
            method: desired ? "POST" : "DELETE",
            accessToken,
            ...(desired ? { body: { variantId } } : {}),
          },
        );
        if (
          authRef.current.owner !== sessionOwner ||
          authRef.current.accessToken !== accessToken
        )
          return false;
        itemsRef.current = desired
          ? [
              ...itemsRef.current.filter(
                (item) =>
                  item.variant?.id !== variantId &&
                  item.variantId !== variantId,
              ),
              result,
            ]
          : itemsRef.current.filter(
              (item) =>
                item.variant?.id !== variantId && item.variantId !== variantId,
            );
        setState((previous) => {
          const optimistic = { ...previous.optimistic },
            pending = { ...previous.pending };
          delete optimistic[variantId];
          delete pending[variantId];
          return { ...previous, items: itemsRef.current, optimistic, pending };
        });
        return true;
      } catch (error) {
        if (
          authRef.current.owner !== sessionOwner ||
          authRef.current.accessToken !== accessToken
        )
          return false;
        setState((previous) => {
          const optimistic = { ...previous.optimistic },
            pending = { ...previous.pending };
          delete optimistic[variantId];
          delete pending[variantId];
          return {
            ...previous,
            optimistic,
            pending,
            error:
              error.message ||
              "Wishlist could not be updated. Your previous selection has been restored.",
          };
        });
        return false;
      } finally {
        if (
          authRef.current.owner === sessionOwner &&
          authRef.current.accessToken === accessToken
        )
          locks.current.delete(variantId);
      }
    },
    [owner, accessToken],
  );
  const current =
    state.owner === owner
      ? state
      : {
          items: [],
          optimistic: {},
          pending: {},
          ready: false,
          loading: Boolean(owner),
          error: "",
        };
  const saved = (id) =>
    Object.prototype.hasOwnProperty.call(current.optimistic, id)
      ? current.optimistic[id]
      : current.items.some(
          (item) => item.variant?.id === id || item.variantId === id,
        );
  const value = {
    items: current.items,
    isSaved: saved,
    isPending: (id) => Boolean(current.pending[id]),
    ready: current.ready,
    loading: current.loading,
    error: current.error,
    refresh,
    change,
  };
  return (
    <WishlistContext.Provider value={value}>
      {children}
      {current.error && owner ? (
        <div
          role="alert"
          className="fixed bottom-6 left-1/2 z-[200] flex w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 items-center gap-3 rounded-xl border border-red-200 bg-white p-4 text-sm text-red-700 shadow-xl"
        >
          <span className="flex-1">{current.error}</span>
          {!current.ready ? (
            <button onClick={refresh} className="font-semibold underline">
              Retry
            </button>
          ) : null}
          <button
            aria-label="Dismiss wishlist error"
            onClick={() => setState((previous) => ({ ...previous, error: "" }))}
          >
            ✕
          </button>
        </div>
      ) : null}
    </WishlistContext.Provider>
  );
}
export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context)
    throw new Error("useWishlist must be used inside WishlistProvider");
  return context;
}
